/**
 * DamageModifiers — Projectile modifier methods, on-hit effects,
 * conditional modifiers, and element integration for AbilityBuilder.
 *
 * These are mixin-style functions that operate on the builder's internal state.
 * AbilityBuilder delegates to these to keep the main class lean.
 */

import type { ProjectileModifiers, HitEffectEntry, HitEffect, ConditionFn, AbilityModifier, ElementType } from './modifiers/types';
import type { AbilityMetadata } from './AbilityRegistry';
import { EventBus } from '../../../../shared/EventBus';
import { gameLogger } from '../../../../utils/GameLogger';
import { getAbilityElements } from '../../../core/HelixGlobals';
import type { ProjectileConfig } from './AbilityBuilder';
// Install the registry before any module-level AbilityBuilder chain calls
// withElement(). Without this side effect, lazy class chunks build against an
// empty global registry and every authored school degrades to "unknown".
import './elements/ElementRegistry';

// =============================================================================
// PROJECTILE MODIFIER HELPERS
// =============================================================================

export function applyPierce(modifiers: ProjectileModifiers, count: number, damageReduction?: number): void {
  modifiers.pierce = { count, damageReduction };
}

export function applyBounce(modifiers: ProjectileModifiers, count: number, range: number, damageReduction?: number): void {
  modifiers.bounce = { count, range, damageReduction };
}

export function applySplit(modifiers: ProjectileModifiers, count: number, spreadAngle: number, damagePercent?: number): void {
  modifiers.split = { count, spreadAngle, damagePercent };
}

export function applyChain(modifiers: ProjectileModifiers, count: number, range: number, damageReduction?: number, vfxId?: string): void {
  modifiers.chain = { count, range, damageReduction, vfxId };
}

export function applyAoeOnImpact(modifiers: ProjectileModifiers, radius: number, damagePercent: number, vfxId?: string): void {
  modifiers.aoeOnImpact = { radius, damagePercent, vfxId };
}

// =============================================================================
// HIT EFFECT HELPERS
// =============================================================================

export function addOnHit(hitEffects: HitEffectEntry[], effect: HitEffect, id?: string): void {
  hitEffects.push({ trigger: 'onHit', effect, id });
}

export function addOnCrit(hitEffects: HitEffectEntry[], effect: HitEffect, id?: string): void {
  hitEffects.push({ trigger: 'onCrit', effect, id });
}

export function addOnKill(hitEffects: HitEffectEntry[], effect: HitEffect, id?: string): void {
  hitEffects.push({ trigger: 'onKill', effect, id });
}

// =============================================================================
// PROC EFFECT
// =============================================================================

export function addProc(
  hitEffects: HitEffectEntry[],
  config: { trigger: 'onHit' | 'onCrit' | 'onDamageTaken' | 'onHeal'; chance: number; cooldownMs?: number; effect: HitEffect; vfxId?: string },
): void {
  const wrappedEffect: HitEffect = (ctx) => {
    if (Math.random() > config.chance) return;
    config.effect(ctx);
    if (config.vfxId) {
      const eb = EventBus.getInstance();
      eb.emit('vfx:spawn', { effectId: config.vfxId, x: ctx.position.x, y: ctx.position.y });
    }
  };
  hitEffects.push({ trigger: config.trigger === 'onDamageTaken' ? 'onHit' : config.trigger, effect: wrappedEffect, id: `proc_${config.trigger}` });
}

// =============================================================================
// ELEMENT APPLICATION
// =============================================================================

export function applyElement(
  element: ElementType,
  metadata: Partial<AbilityMetadata>,
  projectileConfig: ProjectileConfig | null,
  hitEffects: HitEffectEntry[],
): void {
  const ELEMENTS = getAbilityElements();
  const config = ELEMENTS[element];
  if (!config) {
    gameLogger.warn('abilities', `withElement: unknown element ${element}`);
    return;
  }

  metadata.element = element;

  // Auto-apply projectile colors if not already set
  if (projectileConfig) {
    if (projectileConfig.color === undefined) {
      projectileConfig.color = config.palette.primary;
    }
    if (projectileConfig.strokeColor === undefined) {
      projectileConfig.strokeColor = config.palette.accent;
    }
  }

  // Auto-apply default on-hit if element has one and no hit effects set yet
  if (config.defaultOnHit && hitEffects.length === 0) {
    hitEffects.push({ trigger: 'onHit', effect: config.defaultOnHit, id: `element_${element}` });
  }
}

// =============================================================================
// CONDITIONAL MODIFIER
// =============================================================================

export function addConditional(
  conditionals: Array<{ condition: ConditionFn; modifier: AbilityModifier }>,
  condition: ConditionFn,
  modifier: AbilityModifier,
): void {
  conditionals.push({ condition, modifier });
}
